import { Injector, Type } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { LazyElementEntry } from './lazy-element-list';

export const CS_V20_LAZY_ELEMENTS_PRELOAD_EVENT = 'cs-v20-lazy-elements:preload';

export interface CsV20LazyElementsApi {
    /** Start loading one or more lazy tags before they appear in the DOM. */
    preload: (tagNames?: string[]) => void;
}

declare global {
    interface Window {
        CS_V20_LAZY_ELEMENTS?: CsV20LazyElementsApi;
    }
}

const preloadQueue: string[][] = [];

/**
 * Queue a preload request until `registerLazyElements` has run (cs-elements-mf20.js bootstrap).
 * Safe to call from consumer MFEs such as scheduler-v2 before the shared bundle finishes loading.
 */
export function requestLazyElementPreload(tagNames: string[]): void {
    const api = window.CS_V20_LAZY_ELEMENTS;
    if (api?.preload) {
        api.preload(tagNames);
        return;
    }
    preloadQueue.push(tagNames);
}

function flushPreloadQueue(preload: CsV20LazyElementsApi['preload']): void {
    while (preloadQueue.length) {
        preload(preloadQueue.shift());
    }
}

/**
 * Defines each lazy tag's custom element only once its tag actually appears in the document,
 * instead of at bootstrap. This bundle loads globally on every v4 page (see cs-elements-mf20.js
 * in v4/index.html), so most tags are never present when this script itself runs — a boot-time
 * `document.querySelector` sweep alone would miss anything Angular mounts afterwards (panels,
 * toasters, and grids opened later in the session). A MutationObserver catches those too. The
 * browser auto-upgrades any pre-existing un-defined element the instant `customElements.define`
 * runs for its tag, so the initial sweep and the observer can safely both fire `upgrade()`.
 *
 * `preload()` lets consumer MFEs (e.g. scheduler-v2) start fetching heavy chunks in parallel with
 * their own boot, so toolbar filter/search UI is ready when the host mounts those tags.
 */
export function registerLazyElements(entries: LazyElementEntry[], injector: Injector): CsV20LazyElementsApi {
    const byTag = new Map(entries.map((entry) => [entry.tagName, entry]));
    const pending = new Set<string>();

    function upgrade(tagName: string): void {
        if (pending.has(tagName) || customElements.get(tagName)) {
            return;
        }
        pending.add(tagName);
        byTag
            .get(tagName)!
            .loadComponent()
            .then((componentClass: Type<unknown>) => {
                const customEl = createCustomElement(componentClass, { injector });
                customElements.define(tagName, customEl);
            });
    }

    function preload(tagNames?: string[]): void {
        const targets = tagNames?.length ? tagNames : [...byTag.keys()];
        for (const tagName of targets) {
            if (byTag.has(tagName)) {
                upgrade(tagName);
            }
        }
    }

    const api: CsV20LazyElementsApi = { preload };
    window.CS_V20_LAZY_ELEMENTS = api;

    window.addEventListener(CS_V20_LAZY_ELEMENTS_PRELOAD_EVENT, (event: Event) => {
        const tags = (event as CustomEvent<{ tags?: string[] }>).detail?.tags;
        if (Array.isArray(tags) && tags.length) {
            preload(tags);
        }
    });

    flushPreloadQueue(preload);

    byTag.forEach((_entry, tagName) => {
        if (document.querySelector(tagName)) {
            upgrade(tagName);
        }
    });

    if (pending.size === byTag.size) {
        return api;
    }

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (!(node instanceof Element)) {
                    continue;
                }
                byTag.forEach((_entry, tagName) => {
                    if (!pending.has(tagName) && (node.tagName.toLowerCase() === tagName || node.querySelector(tagName))) {
                        upgrade(tagName);
                    }
                });
            }
        }
        if (pending.size === byTag.size) {
            observer.disconnect();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return api;
}
